import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import axiosInstance from 'src/api/axiosInstance';

// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import HeadsetMicRoundedIcon from '@mui/icons-material/HeadsetMicRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import LiveHelpRoundedIcon from '@mui/icons-material/LiveHelpRounded';
import AppVersion from 'src/components/AppVersion';

// hooks
import { useResponsive } from 'src/hooks/use-responsive';
import { Icon } from '@iconify/react';
// components
import Logo from 'src/components/logo';
import Scrollbar from 'src/components/scrollbar';
import { usePathname } from 'src/routes/hooks';
import { NavSectionVertical, NavSectionMini } from 'src/components/nav-section';
import { hideScroll } from 'src/theme/css';

import { Button, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import SetupChecklistWidget from 'src/components/setup-checklist/SetupChecklistWidget';
import Iconify from 'src/components/iconify';
import { HEADER, NAV } from '../config-layout';
import { useNavData } from './config-navigation';
// eslint-disable-next-line import/named
import { setDrawerWidthJS, setHasMounted } from '../../redux/slices/drawerSlice';


// ----------------------------------------------------------------------

export default function NavVertical({ openNav, onCloseNav }) {
  const dispatch = useDispatch();

  const pathname = usePathname();
  const lgUp = useResponsive('up', 'lg');
  const navData = useNavData();

  const [drawerWidth, setDrawerWidth] = useState(NAV.W_VERTICAL);


  const token = sessionStorage.getItem('token');

  const minDrawerWidth = 200;
  const maxDrawerWidth = 500;

  useEffect(() => {
    dispatch(setHasMounted(true));
  }, [dispatch]);

  useEffect(() => {
    onCloseNav();
  }, [pathname, onCloseNav]);

  const handleMouseDown = () => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    const newWidth = e.clientX;
    if (newWidth >= minDrawerWidth && newWidth <= maxDrawerWidth) {
      setDrawerWidth(newWidth);
      dispatch(setDrawerWidthJS(newWidth));
    }
  };

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };



  const renderContent = (
    <>
      <Scrollbar
        sx={{
          pt: 1.5,
          height: 1,
          '& .simplebar-content': {
            display: 'flex',
            flexDirection: 'column',
          },
          '& .simplebar-track.simplebar-vertical': {
            display: 'none',
          },
          '& .simplebar-scrollbar:before': {
            backgroundColor: 'rgba(99, 115, 129, 0.45)',
          },
        }}
      >
        {!lgUp && (
          <Box
            sx={{
              px: 2.5,
              py: 0.75,
              maxHeight: 60,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Logo />
          </Box>
        )}

        {/* ── Setup Checklist Widget (Admin only) ── */}
        <SetupChecklistWidget />

        <NavSectionVertical data={navData} drawerWidth={drawerWidth} sx={{ pt: 0.5 }} />
        <br />

        {/* ── Help & Support Button ─────────────────────── */}

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px dashed rgba(145, 158, 171, 0.2)' }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <AppVersion size="small" variant="inline" showLabel />
          </Stack>
        </Box>
      </Scrollbar>
    </>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: drawerWidth },
      }}
    >
      {lgUp ? (
        <Stack
          sx={{
            bottom: 0,
            position: 'fixed',
            top: { lg: HEADER.H_DESKTOP }, // Start below the header on desktop
            width: drawerWidth,
            bgcolor: 'background.paper',
            borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
          }}
        >
          {drawerWidth < 110 ? (
            <Box sx={{ height: 1, pb: 2, ...hideScroll.y, display: 'flex', flexDirection: 'column' }}>
              <NavSectionMini data={navData} sx={{ pt: 1.5 }} />
              <Box sx={{ flexGrow: 1 }} />

            </Box>
          ) : (
            renderContent
          )}
          <Box
            sx={{
              width: '1px',
              cursor: 'ew-resize',
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'divider',
              opacity: 0.3,
              zIndex: 100,
            }}
            onMouseDown={handleMouseDown}
          />
          {/* Toggle Button for nav-mini */}
          <IconButton
            size="small"
            onClick={() => {
              const newWidth = drawerWidth < 110 ? 280 : 88;
              setDrawerWidth(newWidth);
              dispatch(setDrawerWidthJS(newWidth));
            }}
            sx={{
              p: 0.5,
              top: 22,
              position: 'absolute',
              right: -12,
              zIndex: 101,
              bgcolor: 'background.paper',
              border: (theme) => `solid 1px ${theme.palette.divider}`,
              '&:hover': {
                bgcolor: 'background.neutral',
              },
            }}
          >
            <Iconify
              icon={drawerWidth < 110 ? 'eva:arrow-ios-forward-fill' : 'eva:arrow-ios-back-fill'}
              width={16}
            />
          </IconButton>
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.W_VERTICAL,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}


    </Box>
  );
}

NavVertical.propTypes = {
  onCloseNav: PropTypes.func,
  openNav: PropTypes.bool,
};
